import { MenuItem, UniqueMenuItem } from '@growdash/growintel/generated';
import { useGrowintelService } from '@growdash/growintel/hooks/useGrowintelService';
import sortByField from '@growdash/growintel/utils/sort-by-field';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '../../../hooks/usePathname';
import MenuItemCard from '../components/menu-item-card';
import BrandMenuViewSkeleton from '../fragments/brand-menu-skeleton';

export default function BrandMenuView() {
  const service = useGrowintelService();
  const params = useParams();

  const { data: menuItems, isFetching: menuLoading } = useQuery({
    queryKey: ['brand-menu-items', params?.id],
    queryFn: async (): Promise<UniqueMenuItem[] | undefined | null> => {
      if (!params?.id) return null;
      const response =
        await service?.menuItemsAPI.getUniqueMenuItemsMenuItemsUniqueGet(
          params.id,
          1,
          Number.MAX_SAFE_INTEGER,
        );
      return response?.data.items;
    },
  });

  // Grouping menu items by category
  const groupItems = (items: MenuItem[]) =>
    items.reduce(
      (
        acc: {
          [key: string]: MenuItem[];
        },
        item: MenuItem,
      ) => {
        const key = item.item_category || 'Uncategorized';
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      },
      {},
    );

  if (menuLoading) return <BrandMenuViewSkeleton />;

  if (!menuItems || menuItems.length === 0) {
    return (
      <div className="flex w-full justify-center items-center h-96">
        <p className="text-muted-foreground">No menu item found</p>
      </div>
    );
  }

  return (
    <>
      {Object.entries(groupItems(sortByField(menuItems!, 'item_category'))).map(
        ([category, items]) => (
          <div className="my-8">
            <div className="text-xl font-semibold mb-2 flex items-center gap-1">
              {category}
            </div>
            <div className="w-full grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 gap-y-4 animate-fade-in">
              {items.map((item) => (
                <MenuItemCard {...item} />
              ))}
            </div>
          </div>
        ),
      )}
    </>
  );
}
